import React, { useEffect, useMemo, useState } from 'react';
import sanitizeHtml from '../../utils/sanitizeHtml';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withUploadToken } from '../../utils/fileUrl';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BadgeIcon from '@mui/icons-material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import DescriptionIcon from '@mui/icons-material/Description';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import PublicIcon from '@mui/icons-material/Public';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { Horse } from '@phosphor-icons/react';
import api, { apiBaseURL } from '../../services/api';
import CountryFlagLabel from '../../components/CountryFlagLabel';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { formatDateDisplay } from '../../utils/dateUtils';

// ===== أدوات مساعدة (مطابقة لصفحة الموظّف HorseDetails) =====
function cleanPedigree(html) {
  return html
    ? html
        .replace(/<div class="info-line text-center">.*?<\/div>/gs, '')
        .replace(/<div class="text-center text-small">.*?<\/div>/gs, '')
    : '';
}
function getAge(value) {
  if (!value) return null;
  const birth = new Date(value);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age -= 1;
  return Math.max(age, 0);
}
function ageLabel(age, language) {
  if (age === null) return '-';
  if (language !== 'ar') return `${age} years`;
  if (age === 1) return '1 سنة';
  if (age === 2) return '2 سنتان';
  if (age >= 3 && age <= 10) return `${age} سنوات`;
  return `${age} سنة`;
}
function genderLabel(gender, age, language) {
  if (gender === 'M') {
    if (age !== null && age < 3) return language === 'ar' ? 'مهرة' : 'Filly';
    return language === 'ar' ? 'فرس' : 'Mare';
  }
  if (gender === 'S') {
    if (age !== null && age < 3) return language === 'ar' ? 'مهر' : 'Colt';
    return language === 'ar' ? 'حصان' : 'Stallion';
  }
  return gender || '-';
}
function localizedPair(language, en, ar) {
  return language === 'ar' ? ar || en || '-' : en || ar || '-';
}
function getAssetUrl(value) {
  if (!value) return undefined;
  if (value.startsWith('data') || value.startsWith('http')) return value;
  return withUploadToken(`${apiBaseURL}/uploads/${value}`);
}

function ResponsiveGrid({ children, min = 260, sx }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 2, alignItems: 'stretch', ...sx }}>
      {children}
    </Box>
  );
}
function StatChip({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${COLORS.line}`, bgcolor: soft, borderRadius: 2, p: 1.5, minHeight: 86 }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Avatar sx={{ bgcolor: '#fff', color, width: 38, height: 38 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography noWrap sx={{ color: COLORS.text, fontWeight: 900 }}>{value || '-'}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
function InfoCard({ title, children }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>{title}</Typography>
      <Stack spacing={1}>{children}</Stack>
    </Paper>
  );
}
function DetailRow({ label, value }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} sx={{ py: 1, borderBottom: `1px solid ${COLORS.line}` }}>
      <Typography sx={{ color: COLORS.muted, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ color: COLORS.text, fontWeight: 900, textAlign: { xs: 'start', sm: 'end' } }}>{value || '-'}</Typography>
    </Stack>
  );
}

export default function ClientHorseDetails() {
  const { horseId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const language = i18n.language === 'ar' ? 'ar' : 'en';
  const dir = language === 'ar' ? 'rtl' : 'ltr';
  const ownerId = localStorage.getItem('owner_id');

  const [horse, setHorse] = useState(null);
  const [lookups, setLookups] = useState({ ownerName: '', color: null, breed: null, country: null });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const labels = useMemo(
    () =>
      language === 'ar'
        ? {
            loading: 'جار تحميل تفاصيل الخيل...', back: 'العودة لخيلي', notFound: 'هذا الخيل غير موجود ضمن حسابك',
            age: 'العمر', owner: 'المالك', origin: 'بلد المنشأ', registration: 'التسجيل', identity: 'الهوية',
            rfid: 'رقم الشريحة', gender: 'الجنس', dob: 'تاريخ الميلاد', color: 'اللون', breed: 'السلالة',
            family: 'النسب', father: 'اسم الأب', mother: 'اسم الأم', breeder: 'اسم المربي', documents: 'المستندات',
            registrationNumber: 'رقم التسجيل', registrationDate: 'تاريخ التسجيل', downloadCertificate: 'تحميل شهادة التسجيل',
            noCertificate: 'لا يوجد ملف تسجيل', pedigree: 'شجرة النسب', pedigreeHint: 'عرض منسق من بيانات النسب المسجلة',
            noPedigree: 'لا توجد بيانات نسب', historyTitle: 'السجل الخاص بالخيل', farrier: 'البيطار', vetclinic: 'العيادة',
            currency: 'ر.س', noHistory: 'لا يوجد سجل بعد لهذا الخيل.',
          }
        : {
            loading: 'Loading horse details...', back: 'Back to my horses', notFound: 'This horse was not found in your account',
            age: 'Age', owner: 'Owner', origin: 'Origin', registration: 'Registration', identity: 'Identity',
            rfid: 'RFID Chip Number', gender: 'Gender', dob: 'Date of Birth', color: 'Color', breed: 'Breed',
            family: 'Family', father: 'Father Name', mother: 'Mother Name', breeder: 'Breeder Name', documents: 'Documents',
            registrationNumber: 'Registration Number', registrationDate: 'Registration Date', downloadCertificate: 'Download certificate',
            noCertificate: 'No registration certificate', pedigree: 'Pedigree', pedigreeHint: 'Cleaned view from the registered pedigree source',
            noPedigree: 'No pedigree data available', historyTitle: 'Horse record & history', farrier: 'Farrier', vetclinic: 'Vet clinic',
            currency: 'SAR', noHistory: 'No history yet for this horse.',
          },
    [language]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: horseData } = await api.get(`/horses/${horseId}`);
        if (cancelled) return;
        // أمان: لا يُعرض إلا خيل يخصّ مالك هذا الحساب.
        if (!ownerId || Number(horseData.owner_id) !== Number(ownerId)) {
          setError(labels.notFound);
          setLoading(false);
          return;
        }
        setHorse(horseData);

        const [colorRes, breedRes, countryRes, ownerRes] = await Promise.allSettled([
          horseData.color_id ? api.get(`/horse_colors/${horseData.color_id}`) : Promise.resolve({ data: null }),
          horseData.breed_id ? api.get(`/horse_breeds/${horseData.breed_id}`) : Promise.resolve({ data: null }),
          horseData.country_of_origin_id ? api.get(`/countries/${horseData.country_of_origin_id}`) : Promise.resolve({ data: null }),
          horseData.owner_id ? api.get(`/owners/${horseData.owner_id}`) : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;
        const owner = ownerRes.status === 'fulfilled' ? ownerRes.value.data : null;
        setLookups({
          ownerName: [owner?.first_name, owner?.last_name].filter(Boolean).join(' '),
          color: colorRes.status === 'fulfilled' ? colorRes.value.data : null,
          breed: breedRes.status === 'fulfilled' ? breedRes.value.data : null,
          country: countryRes.status === 'fulfilled' ? countryRes.value.data : null,
        });

        // السجل التاريخي (بيطار + عيادة) — للقراءة فقط.
        const [farrierRes, vetRes] = await Promise.all([
          api.get(`/farrier-records-history/${horseId}`).catch(() => ({ data: [] })),
          api.get(`/vetclinic-records-history/${horseId}`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const farrier = (Array.isArray(farrierRes.data) ? farrierRes.data : []).map((r) => ({
          key: `f-${r.record_id}`, service: 'farrier', date: r.record_date, amount: Number(r.total_amount || 0), notes: r.notes || r.description || '',
        }));
        const vet = (Array.isArray(vetRes.data) ? vetRes.data : []).map((r) => ({
          key: `v-${r.record_id}`, service: 'vetclinic', date: r.record_date, amount: Number(r.total_amount || 0), notes: r.notes || r.diagnosis || r.description || '',
        }));
        setHistory([...farrier, ...vet].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)));
      } catch (err) {
        if (!cancelled) setError(labels.notFound);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [horseId, ownerId, labels.notFound]);

  const age = useMemo(() => getAge(horse?.date_of_birth), [horse]);
  const pedigreeHtml = useMemo(() => sanitizeHtml(cleanPedigree(horse?.pedigree_html)), [horse]);
  const colorName = localizedPair(language, lookups.color?.color_english, lookups.color?.color_arabic);
  const breedName = localizedPair(language, lookups.breed?.breed_english, lookups.breed?.breed_arabic);
  const countryName = localizedPair(language, lookups.country?.country_english, lookups.country?.country_arabic);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={26} />
          <Typography>{labels.loading}</Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !horse) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{error || labels.notFound}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/my-horses')} sx={{ mt: 2, color: COLORS.text, fontWeight: 900 }}>
          {labels.back}
        </Button>
      </Box>
    );
  }

  return (
    <Box dir={dir} sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
        {/* رأس الصفحة — مطابق لصفحة الموظّف، بلا زر تعديل (قراءة فقط) */}
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5, background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <Avatar
                src={getAssetUrl(horse.image)}
                alt={horse.name}
                sx={{ width: { xs: 126, sm: 150 }, height: { xs: 126, sm: 150 }, bgcolor: COLORS.goldSoft, color: COLORS.gold, fontSize: 44, fontWeight: 900, border: `4px solid ${COLORS.card}`, boxShadow: '0 12px 28px rgba(91,75,39,0.12)' }}
              >
                {(horse.name_arabic || horse.name || '-').charAt(0)}
              </Avatar>
              <Box sx={{ textAlign: { xs: 'center', sm: 'start' } }}>
                <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{horse.name || '-'}</Typography>
                <Typography variant="h5" sx={{ color: COLORS.gold, fontWeight: 900, mt: 0.4 }}>{horse.name_arabic || '-'}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'center', sm: 'flex-start' }} sx={{ mt: 1.2 }}>
                  <Chip size="small" icon={horse.gender === 'S' ? <MaleIcon /> : <FemaleIcon />} label={genderLabel(horse.gender, age, language)} sx={{ bgcolor: horse.gender === 'S' ? COLORS.blueSoft : COLORS.roseSoft, color: COLORS.text, fontWeight: 800 }} />
                  <Chip size="small" icon={<QrCode2Icon />} label={horse.rfid_chip_number || 'RFID -'} sx={{ bgcolor: COLORS.card, color: COLORS.text, fontWeight: 800 }} />
                </Stack>
              </Box>
            </Stack>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/my-horses')} sx={{ color: COLORS.text, fontWeight: 900 }}>
              {labels.back}
            </Button>
          </Stack>
        </Paper>

        {/* بطاقات إحصائية */}
        <ResponsiveGrid min={220} sx={{ mb: 2.5 }}>
          <StatChip icon={<CakeIcon />} label={labels.age} value={ageLabel(age, language)} />
          <StatChip icon={<BadgeIcon />} label={labels.owner} value={lookups.ownerName} color={COLORS.blue} soft={COLORS.blueSoft} />
          <StatChip icon={<PublicIcon />} label={labels.origin} value={<CountryFlagLabel country={countryName} />} color={COLORS.green} soft={COLORS.greenSoft} />
          <StatChip icon={<DescriptionIcon />} label={labels.registration} value={horse.registration_number || '-'} color={COLORS.rose} soft={COLORS.roseSoft} />
        </ResponsiveGrid>

        {/* بطاقات التفاصيل */}
        <ResponsiveGrid min={340} sx={{ mb: 2.5 }}>
          <InfoCard title={labels.identity}>
            <DetailRow label={labels.rfid} value={horse.rfid_chip_number} />
            <DetailRow label={labels.gender} value={genderLabel(horse.gender, age, language)} />
            <DetailRow label={labels.dob} value={<span className="date-ltr">{formatDateDisplay(horse.date_of_birth)}</span>} />
            <DetailRow label={labels.color} value={colorName} />
            <DetailRow label={labels.breed} value={breedName} />
          </InfoCard>

          <InfoCard title={labels.family}>
            <DetailRow label={labels.father} value={localizedPair(language, horse.father_name, horse.father_name_arabic)} />
            <DetailRow label={labels.mother} value={localizedPair(language, horse.mother_name, horse.mother_name_arabic)} />
            <DetailRow label={labels.breeder} value={localizedPair(language, horse.breeder_name, horse.breeder_name_arabic)} />
            <DetailRow label={labels.owner} value={lookups.ownerName} />
          </InfoCard>

          <InfoCard title={labels.documents}>
            <DetailRow label={labels.registrationNumber} value={horse.registration_number} />
            <DetailRow label={labels.registrationDate} value={<span className="date-ltr">{formatDateDisplay(horse.registration_date)}</span>} />
            <Divider sx={{ borderColor: COLORS.line, my: 1 }} />
            {horse.registration_certificate ? (
              <Button variant="contained" startIcon={<DescriptionIcon />} href={getAssetUrl(horse.registration_certificate)} target="_blank" rel="noopener noreferrer" sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>
                {labels.downloadCertificate}
              </Button>
            ) : (
              <Chip label={labels.noCertificate} sx={{ bgcolor: COLORS.cardSoft, color: COLORS.muted, fontWeight: 800 }} />
            )}
          </InfoCard>
        </ResponsiveGrid>

        {/* شجرة النسب */}
        <Paper elevation={0} sx={{ ...cardSx, p: 2.2, mb: 2.5 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{labels.pedigree}</Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>{labels.pedigreeHint}</Typography>
          </Box>
          {pedigreeHtml ? (
            <Box sx={{ border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: COLORS.cardSoft, p: 2, overflowX: 'auto', '& table': { mx: 'auto' } }} dangerouslySetInnerHTML={{ __html: pedigreeHtml }} />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: COLORS.muted, bgcolor: COLORS.cardSoft, borderRadius: 2 }}>{labels.noPedigree}</Box>
          )}
        </Paper>

        {/* السجل التاريخي (للقراءة فقط) */}
        <Paper elevation={0} sx={{ ...cardSx, p: 2.2 }}>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>{labels.historyTitle}</Typography>
          {history.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: COLORS.muted, bgcolor: COLORS.cardSoft, borderRadius: 2 }}>{labels.noHistory}</Box>
          ) : (
            <Stack spacing={1.2}>
              {history.map((rec) => (
                <Paper key={rec.key} elevation={0} sx={{ border: `1px solid ${COLORS.line}`, borderRadius: 2, p: 1.6, bgcolor: COLORS.cardSoft }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: '#fff', color: COLORS.gold }}>
                        {rec.service === 'farrier' ? <BadgeIcon fontSize="small" /> : <Horse size={18} weight="fill" />}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{rec.service === 'farrier' ? labels.farrier : labels.vetclinic}</Typography>
                        {rec.notes ? <Typography variant="caption" sx={{ color: COLORS.muted, wordBreak: 'break-word' }}>{rec.notes}</Typography> : null}
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="caption" className="date-ltr" sx={{ color: COLORS.muted, fontWeight: 800 }}>{formatDateDisplay(rec.date)}</Typography>
                      {rec.amount > 0 ? <Typography sx={{ color: COLORS.text, fontWeight: 900, whiteSpace: 'nowrap' }}>{rec.amount.toLocaleString()} {labels.currency}</Typography> : null}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
